import { useRef, useState } from "react";

import {
  LABEEB_CATALOG,
  labeebDomainsOf,
  labeebTenantSettingsPath,
  type LabeebCatalog,
  type LabeebTenantSettings,
} from "../../api/labeeb";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useResource, type Resource } from "../../hooks/useResource";
import { useWrite } from "../../hooks/useWrite";
import { Gate } from "../authority/Gate";
import { ResourceView } from "../states";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { WriteResult } from "../write/EffectTiming";
import { WriteFailure } from "../write/WriteFailure";

type DomainMode = "all" | "some" | "none";

/**
 * Which domains this business asked for.
 *
 * `"all"`, a list of catalog names, or `[]`. An empty list is a real choice —
 * no domain — and is not the same as leaving the field off a later write.
 * What actually runs is the effective table above this control; the
 * environment can still hold a domain off after the business asks for it.
 *
 * The table and this control share one settings read. A reload that fails
 * must not unmount the form: the last document stays on screen, and
 * `WriteFailure` with it. A different merchant is not that reload — the
 * edit in progress belongs to the merchant who was on screen, and saving
 * it would write their domains onto the next one.
 */
export function LabeebDomainControl({
  config,
  tenantId,
  settings,
  reload,
}: {
  config: AppConfig;
  tenantId: string;
  settings: Resource<LabeebTenantSettings>;
  reload: () => void;
}) {
  const catalog = useResource<LabeebCatalog>(config, LABEEB_CATALOG);
  const write = useWrite(config);
  const [confirming, setConfirming] = useState(false);
  const [mode, setMode] = useState<DomainMode | null>(null);
  const [picked, setPicked] = useState<string[] | null>(null);
  const held = useRef<LabeebTenantSettings | null>(null);
  // The previous merchant's `ready` payload is still in `settings` until the
  // path change reaches `loading`. Copying it back into `held` would put
  // their document under the merchant now on screen.
  const stale = useRef(false);
  const [editedFor, setEditedFor] = useState(tenantId);
  if (editedFor !== tenantId) {
    setEditedFor(tenantId);
    setMode(null);
    setPicked(null);
    held.current = null;
    stale.current = true;
  }
  if (settings.kind === "loading") stale.current = false;
  if (settings.kind === "ready" && !stale.current) held.current = settings.data;
  const doc = held.current;
  const busy = write.state.kind === "submitting";

  const asked = doc?.settings?.enabled_domains;
  const storedMode: DomainMode =
    asked === "all" ? "all" : Array.isArray(asked) && asked.length === 0 ? "none" : "some";
  const storedList = Array.isArray(asked) ? asked : [];
  const currentMode = mode ?? storedMode;
  const currentPicked = picked ?? storedList;
  const version = doc?.version;
  const catalogDomains = catalog.state.kind === "ready" ? labeebDomainsOf(catalog.state.data) : [];

  const same = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));
  const dirty =
    currentMode !== storedMode || (currentMode === "some" && !same(currentPicked, storedList));

  function choose(next: DomainMode) {
    setMode(next);
    if (next === "some" && picked === null) {
      setPicked(asked === "all" ? [...catalogDomains] : [...storedList]);
    }
  }

  function submit(reason: string) {
    if (version === undefined) return;
    const enabled_domains =
      currentMode === "all"
        ? "all"
        : currentMode === "none"
          ? []
          : catalogDomains.filter((d) => currentPicked.includes(d));
    void write
      .submit(labeebTenantSettingsPath(tenantId), {
        method: "PATCH",
        body: JSON.stringify({
          expected_version: version,
          reason,
          settings: { enabled_domains },
        }),
      })
      .then((r) => {
        if (!r) return;
        setMode(null);
        setPicked(null);
        reload();
      });
    setConfirming(false);
  }

  const form = !doc ? null : (
    <div className="mt-4 flex flex-col gap-3">
      {catalog.state.kind !== "ready" ? (
        <ResourceView state={catalog.state} what="the catalog" onRetry={catalog.reload} />
      ) : (
        <>
          <fieldset className="flex flex-col gap-2">
            <legend className="text-xs font-medium text-slate-500 dark:text-slate-400">
              What this business may use
            </legend>
            {(
              [
                ["all", "All domains"],
                ["some", "Only these domains"],
                ["none", "No domains"],
              ] as const
            ).map(([value, label]) => (
              <label key={value} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name={`domains-${tenantId}`}
                  checked={currentMode === value}
                  onChange={() => choose(value)}
                />
                {label}
              </label>
            ))}
          </fieldset>

          {currentMode === "some" && (
            <fieldset className="flex flex-col gap-2 pl-1">
              <legend className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Domains
              </legend>
              {catalogDomains.map((d) => (
                <label key={d} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={currentPicked.includes(d)}
                    onChange={(e) =>
                      setPicked(
                        e.target.checked
                          ? [...currentPicked, d]
                          : currentPicked.filter((x) => x !== d),
                      )
                    }
                  />
                  <span className="ref">{d}</span>
                </label>
              ))}
            </fieldset>
          )}

          <p className="text-xs/5 text-slate-500 dark:text-slate-400">
            An empty choice means no domain, not “leave it as it was”. That turns off agent domains
            only — plugins and suspension are separate. Test in a new message after saving.
          </p>

          <Gate need="settings:write">
            <Button
              disabled={busy || !dirty || version === undefined}
              onClick={() => setConfirming(true)}
            >
              Save domains…
            </Button>
          </Gate>
        </>
      )}
    </div>
  );

  return (
    <>
      {form}
      {write.state.kind === "done" && (
        <WriteResult title="Domains saved" onDismiss={write.reset}>
          <p className="text-sm">This business picks the change up on its next turn.</p>
        </WriteResult>
      )}
      <WriteFailure
        state={write.state}
        onDismiss={write.reset}
        onReread={() => {
          write.reset();
          setMode(null);
          setPicked(null);
          reload();
        }}
      />
      <ConfirmDialog
        open={confirming}
        title="Change which domains this business may use"
        confirmLabel="Save"
        requireReason
        reasonHint="e.g. DA-1500, they do not use inventory"
        busy={busy}
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
        effect="Applies from this business's next turn. A domain the environment has off stays off."
      >
        <p>
          {currentMode === "none"
            ? "No domain will be enabled for this business."
            : currentMode === "all"
              ? "Every domain the environment allows will be available."
              : "Only the domains still checked will be available."}
        </p>
      </ConfirmDialog>
    </>
  );
}
